// AdminBlogPage.js — Phase Blog (2026-05-30)
//
// Admin queue for mchatai.com/blog. Two Firestore collections:
//   blog_drafts/{id}   — work-in-progress (pipeline output OR manual paste)
//   blog_posts/{slug}  — published, public-read; rendered alongside the static
//                        manifest by BlogPage / BlogPostPage.
//
// Flow:
//   1. Pipeline (or you) writes a doc to blog_drafts. Status starts "draft".
//   2. Admin reviews here, edits fields/markdown, clicks "Mark Ready" or
//      "Publish Now".
//   3. Publish copies the draft into blog_posts/{slug}, marks the draft
//      status="published" with publishedAt. The public blog pulls the new
//      post live without a rebuild.
//
// "New post" button creates a draft from a paste-in form; you can hit
// "Publish Now" in the same modal for one-step manual posting.
//
// Auth: same UID allowlist + custom-claim pattern as the other admin pages.
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import {
  getFirestore, collection, query, orderBy, limit, getDocs,
  doc, setDoc, updateDoc, deleteDoc, serverTimestamp, getDoc,
} from 'firebase/firestore';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './App.css';

const ADMIN_UIDS = ['GI1GHQ7wpLQKjJyf9QRQATyw5e33', 'vGB6BL3iJfh32aWZ0HA93nUV2013'];
const STATIC_MANIFEST_URL = '/blog-content/_index.json';

const STATUS_FILTERS = [
  { id: 'draft',     label: 'Draft',     predicate: (d) => (d.status || 'draft') === 'draft' },
  { id: 'ready',     label: 'Ready',     predicate: (d) => d.status === 'ready' },
  { id: 'published', label: 'Published', predicate: (d) => d.status === 'published' },
  { id: 'discarded', label: 'Discarded', predicate: (d) => d.status === 'discarded' },
  { id: 'all',       label: 'All',       predicate: () => true },
];

const PALETTE = {
  bg: '#0f172a', panel: '#1e293b', panelAlt: '#0b1220', border: '#334155',
  text: '#f8fafc', muted: '#94a3b8', dim: '#64748b',
  accent: '#3b82f6', success: '#10b981', warn: '#f59e0b', error: '#ef4444',
};

function slugify(text) {
  return (text || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 60);
}

function estimateReadingMinutes(markdown) {
  const words = (markdown || '').trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 220));
}

function formatTimestamp(ts) {
  if (!ts) return '—';
  let d;
  if (typeof ts.toDate === 'function') d = ts.toDate();
  else if (ts.seconds) d = new Date(ts.seconds * 1000);
  else if (typeof ts === 'string') d = new Date(ts);
  else return '—';
  if (isNaN(d)) return '—';
  return d.toLocaleString();
}

function statusColor(status) {
  switch (status) {
    case 'draft':     return PALETTE.muted;
    case 'ready':     return PALETTE.accent;
    case 'published': return PALETTE.success;
    case 'discarded': return PALETTE.dim;
    default:          return PALETTE.dim;
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// Shared markdown rendering — mirrors BlogPostPage.js so the admin preview
// looks like the live post. Light background here vs the dark admin chrome.
// ──────────────────────────────────────────────────────────────────────────────
const markdownComponents = {
  h1: ({ children }) => <h1 style={{ fontSize: '1.9rem', fontWeight: 800, color: '#111827', margin: '0 0 18px', lineHeight: 1.2 }}>{children}</h1>,
  h2: ({ children }) => <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: '#111827', margin: '28px 0 10px' }}>{children}</h2>,
  h3: ({ children }) => <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#111827', margin: '20px 0 6px' }}>{children}</h3>,
  p: ({ children }) => <p style={{ color: '#374151', lineHeight: 1.7, marginBottom: 14, fontSize: '0.98rem' }}>{children}</p>,
  ul: ({ children }) => <ul style={{ color: '#374151', lineHeight: 1.7, marginBottom: 14, paddingLeft: 22, fontSize: '0.98rem' }}>{children}</ul>,
  ol: ({ children }) => <ol style={{ color: '#374151', lineHeight: 1.7, marginBottom: 14, paddingLeft: 22, fontSize: '0.98rem' }}>{children}</ol>,
  a: ({ href, children, ...rest }) => <a href={href} style={{ color: '#3b82f6', textDecoration: 'underline' }} {...rest}>{children}</a>,
  code: ({ inline, children, ...rest }) => inline
    ? <code style={{ background: '#f3f4f6', padding: '1px 6px', borderRadius: 4, fontSize: '0.9em', color: '#111827' }} {...rest}>{children}</code>
    : <code {...rest}>{children}</code>,
  pre: ({ children }) => <pre style={{ background: '#0f172a', color: '#e2e8f0', padding: 14, borderRadius: 8, overflowX: 'auto', fontSize: '0.85rem', lineHeight: 1.5, marginBottom: 16 }}>{children}</pre>,
  blockquote: ({ children }) => <blockquote style={{ borderLeft: '3px solid #d1d5db', paddingLeft: 14, color: '#4b5563', margin: '14px 0', fontStyle: 'italic' }}>{children}</blockquote>,
};

// ──────────────────────────────────────────────────────────────────────────────
// Reusable post-edit form (used in both the "New post" modal and the edit drawer)
// ──────────────────────────────────────────────────────────────────────────────
function PostForm({ value, onChange, autoSlug = false }) {
  const v = value;
  const set = (patch) => onChange({ ...v, ...patch });
  const [showPreview, setShowPreview] = useState(false);

  // Auto-slug from title when user hasn't manually edited slug
  useEffect(() => {
    if (autoSlug && v.title && !v._slugTouched) {
      const next = slugify(v.title);
      if (next !== v.slug) set({ slug: next });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v.title]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Field label="Title">
        <input
          type="text" value={v.title || ''}
          onChange={(e) => set({ title: e.target.value })}
          style={inputStyle}
          placeholder="What it is, in 90 seconds"
        />
      </Field>
      <Field label="Slug (URL)">
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: PALETTE.dim, fontSize: '0.78rem' }}>mchatai.com/blog/</span>
          <input
            type="text" value={v.slug || ''}
            onChange={(e) => set({ slug: slugify(e.target.value), _slugTouched: true })}
            style={{ ...inputStyle, flex: 1 }}
            placeholder="auto-from-title"
          />
        </div>
      </Field>
      <Field label="Summary (1-2 sentences)">
        <textarea
          value={v.summary || ''}
          onChange={(e) => set({ summary: e.target.value })}
          rows={2}
          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
        />
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field label="Author">
          <input type="text" value={v.author || ''} onChange={(e) => set({ author: e.target.value })} style={inputStyle} placeholder="Lawrence Norman" />
        </Field>
        <Field label="Tags (comma-separated)">
          <input
            type="text"
            value={(v.tags || []).join(', ')}
            onChange={(e) => set({ tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
            style={inputStyle}
            placeholder="intro, creator"
          />
        </Field>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field label="Series (optional)">
          <input type="text" value={v.series || ''} onChange={(e) => set({ series: e.target.value })} style={inputStyle} placeholder="Built in mChatAI" />
        </Field>
        <Field label="Hero image URL (optional)">
          <input type="text" value={v.heroImage || ''} onChange={(e) => set({ heroImage: e.target.value })} style={inputStyle} placeholder="https://…" />
        </Field>
      </div>

      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <span style={{ color: PALETTE.muted, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 }}>
            Markdown body
          </span>
          <button
            type="button"
            onClick={() => setShowPreview(s => !s)}
            style={{
              padding: '4px 10px', background: showPreview ? PALETTE.accent : 'transparent',
              color: showPreview ? 'white' : PALETTE.dim, border: `1px solid ${PALETTE.border}`,
              borderRadius: 4, cursor: 'pointer', fontSize: '0.75rem',
            }}>
            {showPreview ? 'Edit' : 'Preview'}
          </button>
        </div>
        {showPreview ? (
          <div style={{
            background: '#fff', borderRadius: 6, padding: 16,
            border: `1px solid ${PALETTE.border}`, maxHeight: 480, overflowY: 'auto',
          }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {v.markdown || '_(empty)_'}
            </ReactMarkdown>
          </div>
        ) : (
          <textarea
            value={v.markdown || ''}
            onChange={(e) => set({ markdown: e.target.value })}
            rows={20}
            style={{
              ...inputStyle, width: '100%', boxSizing: 'border-box',
              fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
              fontSize: '0.84rem', lineHeight: 1.5,
            }}
            placeholder={'# Heading\n\nPaste markdown here…'}
          />
        )}
        <div style={{ color: PALETTE.dim, fontSize: '0.72rem', marginTop: 4 }}>
          {estimateReadingMinutes(v.markdown)} min read · {(v.markdown || '').length} chars
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ color: PALETTE.muted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700, marginBottom: 4 }}>
        {label}
      </div>
      {children}
    </div>
  );
}

const inputStyle = {
  background: PALETTE.panelAlt, color: PALETTE.text, border: `1px solid ${PALETTE.border}`,
  padding: '7px 10px', borderRadius: 4, fontSize: '0.85rem', width: '100%', boxSizing: 'border-box',
};

// ──────────────────────────────────────────────────────────────────────────────
// New post modal (reuses PostForm)
// ──────────────────────────────────────────────────────────────────────────────
function NewPostModal({ onSave, onPublish, onCancel, defaultAuthor }) {
  const [v, setV] = useState({
    title: '', slug: '', summary: '', author: defaultAuthor || '', tags: [],
    series: '', heroImage: '', markdown: '',
  });
  const [busy, setBusy] = useState(null);

  const canSave = v.title.trim() && v.slug.trim() && v.markdown.trim();

  return (
    <div style={modalBackdrop}>
      <div style={{ ...modalCard, maxWidth: 760 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h2 style={{ color: PALETTE.text, margin: 0, fontSize: '1.2rem' }}>New blog post</h2>
          <button onClick={onCancel} style={closeBtn}>×</button>
        </div>
        <PostForm value={v} onChange={setV} autoSlug={true} />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onCancel} style={ghostBtn} disabled={!!busy}>Cancel</button>
          <button
            onClick={async () => { setBusy('draft'); try { await onSave(v); } finally { setBusy(null); } }}
            disabled={!canSave || !!busy} style={ghostBtn}>
            {busy === 'draft' ? 'Saving…' : 'Save as Draft'}
          </button>
          <button
            onClick={async () => { setBusy('publish'); try { await onPublish(v); } finally { setBusy(null); } }}
            disabled={!canSave || !!busy} style={primaryBtn}>
            {busy === 'publish' ? 'Publishing…' : 'Publish Now'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Detail drawer for an existing draft
// ──────────────────────────────────────────────────────────────────────────────
function DraftDrawer({ draft, onClose, onSave, onMarkReady, onPublish, onDiscard, onUnpublish, onDelete }) {
  const [v, setV] = useState(draft);
  const [busy, setBusy] = useState(null);
  const dirty = JSON.stringify(v) !== JSON.stringify(draft);

  useEffect(() => { setV(draft); }, [draft]);

  const wrap = (kind, fn) => async (...args) => {
    setBusy(kind);
    try { await fn(...args); } finally { setBusy(null); }
  };

  return (
    <div style={{
      background: PALETTE.panel, borderRadius: 8, padding: 18,
      border: `1px solid ${PALETTE.border}`, position: 'sticky', top: 20,
      alignSelf: 'start', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div>
          <div style={{ color: PALETTE.dim, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
            {draft.status} · {draft.source || 'manual'}
          </div>
          <h2 style={{ color: PALETTE.text, margin: '2px 0', fontSize: '1.1rem' }}>{draft.title || '(untitled)'}</h2>
          <div style={{ color: PALETTE.dim, fontSize: '0.75rem' }}>
            {formatTimestamp(draft.updatedAt || draft.createdAt)}
            {draft.publishedAt && ` · published ${formatTimestamp(draft.publishedAt)}`}
          </div>
        </div>
        <button onClick={onClose} style={closeBtn}>×</button>
      </div>

      <PostForm value={v} onChange={setV} autoSlug={false} />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16 }}>
        <button
          onClick={wrap('save', () => onSave(v))}
          disabled={!dirty || !!busy}
          style={dirty ? primaryBtn : ghostBtn}>
          {busy === 'save' ? 'Saving…' : (dirty ? 'Save changes' : 'No changes')}
        </button>

        {draft.status !== 'published' && draft.status !== 'discarded' && (
          <>
            {draft.status !== 'ready' && (
              <button onClick={wrap('ready', () => onMarkReady(v))} disabled={!!busy} style={ghostBtn}>
                {busy === 'ready' ? '…' : 'Mark Ready'}
              </button>
            )}
            <button onClick={wrap('publish', () => onPublish(v))} disabled={!!busy} style={successBtn}>
              {busy === 'publish' ? 'Publishing…' : 'Publish'}
            </button>
            <button onClick={wrap('discard', () => onDiscard())} disabled={!!busy} style={dangerBtn}>
              Discard
            </button>
          </>
        )}

        {draft.status === 'published' && (
          <>
            <a
              href={`/blog/${draft.publishedSlug || draft.slug}`}
              target="_blank" rel="noopener noreferrer"
              style={{ ...ghostBtn, textDecoration: 'none', display: 'inline-block' }}>
              View live →
            </a>
            <button onClick={wrap('unpublish', () => onUnpublish())} disabled={!!busy} style={ghostBtn}>
              {busy === 'unpublish' ? '…' : 'Unpublish'}
            </button>
          </>
        )}

        {(draft.status === 'discarded' || draft.status === 'published') && (
          <button onClick={wrap('delete', () => onDelete())} disabled={!!busy} style={dangerBtn}>
            Delete draft
          </button>
        )}
      </div>
    </div>
  );
}

const primaryBtn = { padding: '8px 14px', background: PALETTE.accent, color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 };
const successBtn = { ...primaryBtn, background: PALETTE.success };
const ghostBtn   = { padding: '8px 14px', background: 'transparent', color: PALETTE.text, border: `1px solid ${PALETTE.border}`, borderRadius: 4, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 };
const dangerBtn  = { ...ghostBtn, color: PALETTE.error, borderColor: PALETTE.error + '55' };
const closeBtn   = { background: 'transparent', color: PALETTE.dim, border: 'none', cursor: 'pointer', fontSize: '1.2rem', padding: '4px 8px' };
const modalBackdrop = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', zIndex: 1000, paddingTop: 40, overflowY: 'auto' };
const modalCard = { background: PALETTE.panel, padding: 20, borderRadius: 10, border: `1px solid ${PALETTE.border}`, width: '92%', maxHeight: 'calc(100vh - 80px)', overflowY: 'auto' };

// ──────────────────────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────────────────────
export default function AdminBlogPage() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [staticSlugs, setStaticSlugs] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('draft');
  const [selected, setSelected] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [actionLog, setActionLog] = useState([]);

  // --- Auth gate ---
  useEffect(() => {
    const auth = getAuth();
    const unsub = auth.onAuthStateChanged(async (u) => {
      setUser(u);
      if (u && (ADMIN_UIDS.includes(u.uid) || (await u.getIdTokenResult()).claims?.role === 'admin')) {
        setIsAdmin(true);
      } else {
        setIsAdmin(false);
      }
      setAuthChecked(true);
    });
    return () => unsub();
  }, []);

  // --- Fetch static manifest slugs (for collision check) ---
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(STATIC_MANIFEST_URL, { cache: 'no-cache' });
        if (r.ok) {
          const m = await r.json();
          setStaticSlugs(new Set((m.posts || []).map(p => p.slug)));
        }
      } catch (e) { /* manifest may not exist on dev; fine */ }
    })();
  }, []);

  // --- Firestore fetch ---
  const refresh = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const db = getFirestore();
      const q = query(collection(db, 'blog_drafts'), orderBy('updatedAt', 'desc'), limit(200));
      const snap = await getDocs(q);
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setDrafts(data);
      // Keep selected in sync if it's in the new data
      if (selected) {
        const fresh = data.find(d => d.id === selected.id);
        if (fresh) setSelected(fresh);
      }
    } catch (err) {
      log('error', `Load failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  useEffect(() => { if (isAdmin) refresh(); }, [isAdmin, refresh]);

  function log(kind, message) {
    setActionLog(prev => [{ ts: new Date().toLocaleTimeString(), kind, message }, ...prev].slice(0, 10));
  }

  // --- Slug uniqueness check (against static manifest + blog_posts) ---
  async function slugIsUnique(slug, excludeDraftId = null) {
    if (!slug) return false;
    if (staticSlugs.has(slug)) return false;
    const db = getFirestore();
    const postSnap = await getDoc(doc(db, 'blog_posts', slug));
    if (postSnap.exists()) {
      // Allow if the existing post was published from this draft (re-publish)
      if (postSnap.data().sourceDraftId === excludeDraftId) return true;
      return false;
    }
    return true;
  }

  // --- Mutations ---
  async function createDraft(v, source = 'manual') {
    const db = getFirestore();
    const ref = doc(collection(db, 'blog_drafts'));
    const data = {
      slug: v.slug, title: v.title, summary: v.summary || '',
      author: v.author || '', tags: v.tags || [],
      markdown: v.markdown, heroImage: v.heroImage || '', series: v.series || '',
      status: 'draft', source,
      createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
    };
    await setDoc(ref, data);
    log('ok', `Draft saved: ${v.title}`);
    await refresh();
    return ref.id;
  }

  async function saveDraft(draftId, v) {
    const db = getFirestore();
    await updateDoc(doc(db, 'blog_drafts', draftId), {
      slug: v.slug, title: v.title, summary: v.summary || '',
      author: v.author || '', tags: v.tags || [],
      markdown: v.markdown, heroImage: v.heroImage || '', series: v.series || '',
      updatedAt: serverTimestamp(),
    });
    log('ok', `Saved: ${v.title}`);
    await refresh();
  }

  async function markReady(draftId) {
    const db = getFirestore();
    await updateDoc(doc(db, 'blog_drafts', draftId), { status: 'ready', updatedAt: serverTimestamp() });
    log('ok', 'Marked ready');
    await refresh();
  }

  async function publishDraft(draftId, v) {
    if (!await slugIsUnique(v.slug, draftId)) {
      log('error', `Slug "${v.slug}" already taken — pick another`);
      throw new Error('Slug taken');
    }
    const db = getFirestore();
    // Write to blog_posts/{slug}
    const postData = {
      slug: v.slug, title: v.title, summary: v.summary || '',
      author: v.author || '', tags: v.tags || [],
      markdown: v.markdown, heroImage: v.heroImage || '', series: v.series || '',
      readingMinutes: estimateReadingMinutes(v.markdown),
      publishedAt: serverTimestamp(),
      sourceDraftId: draftId,
    };
    await setDoc(doc(db, 'blog_posts', v.slug), postData);
    // Mark draft published
    await updateDoc(doc(db, 'blog_drafts', draftId), {
      status: 'published',
      publishedSlug: v.slug,
      publishedAt: serverTimestamp(),
      slug: v.slug, title: v.title, summary: v.summary || '',
      author: v.author || '', tags: v.tags || [],
      markdown: v.markdown, heroImage: v.heroImage || '', series: v.series || '',
      updatedAt: serverTimestamp(),
    });
    log('ok', `Published: /blog/${v.slug}`);
    await refresh();
  }

  async function discardDraft(draftId) {
    const db = getFirestore();
    await updateDoc(doc(db, 'blog_drafts', draftId), { status: 'discarded', updatedAt: serverTimestamp() });
    log('ok', 'Discarded');
    await refresh();
  }

  async function unpublishDraft(draftId, slug) {
    const db = getFirestore();
    await deleteDoc(doc(db, 'blog_posts', slug));
    await updateDoc(doc(db, 'blog_drafts', draftId), {
      status: 'draft', publishedSlug: null, updatedAt: serverTimestamp(),
    });
    log('ok', `Unpublished /blog/${slug}`);
    await refresh();
  }

  async function deleteDraft(draftId) {
    const db = getFirestore();
    await deleteDoc(doc(db, 'blog_drafts', draftId));
    log('ok', 'Draft deleted');
    setSelected(null);
    await refresh();
  }

  // --- Modal callbacks ---
  async function handleNewSave(v) {
    if (!await slugIsUnique(v.slug)) {
      log('error', `Slug "${v.slug}" already taken — pick another`);
      throw new Error('Slug taken');
    }
    await createDraft(v);
    setShowNewModal(false);
  }
  async function handleNewPublish(v) {
    const id = await createDraft(v);
    // Immediately publish
    try { await publishDraft(id, v); } catch (e) { /* slug-taken etc. */ }
    setShowNewModal(false);
  }

  // --- Render: auth states ---
  if (!authChecked) {
    return <div style={{ padding: 40, color: PALETTE.text, background: PALETTE.bg, minHeight: '100vh' }}>Checking auth…</div>;
  }
  if (!user) {
    return (
      <div style={{ padding: 40, color: PALETTE.text, background: PALETTE.bg, minHeight: '100vh' }}>
        <h1>Sign-in required</h1>
        <p>This page is admin-only. <Link to="/login">Sign in</Link>.</p>
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div style={{ padding: 40, color: PALETTE.text, background: PALETTE.bg, minHeight: '100vh' }}>
        <h1>Access denied</h1>
        <p>You're signed in as {user.email || user.uid} but this page is admin-only.</p>
        <p><Link to="/admin">← Admin home</Link></p>
      </div>
    );
  }

  // --- Filtered list ---
  const predicate = STATUS_FILTERS.find(f => f.id === statusFilter)?.predicate || (() => true);
  const filtered = drafts.filter(predicate);

  return (
    <div className="mp-page" style={{ background: PALETTE.bg, color: PALETTE.text, minHeight: '100vh' }}>
      <section style={{ maxWidth: 1500, margin: '0 auto', padding: '32px 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
          <div>
            <h1 style={{ color: PALETTE.text, fontSize: '1.8rem', fontWeight: 800, margin: 0 }}>Blog</h1>
            <p style={{ color: PALETTE.dim, margin: '4px 0 0 0' }}>
              Review pipeline drafts, edit, publish to mchatai.com/blog. Or paste your own.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={refresh} disabled={loading} style={ghostBtn}>{loading ? 'Loading…' : 'Refresh'}</button>
            <button onClick={() => setShowNewModal(true)} style={primaryBtn}>+ New post</button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 16, marginBottom: 14, fontSize: '0.82rem' }}>
          <Link to="/admin" style={{ color: PALETTE.accent }}>← Admin home</Link>
          <Link to="/admin/tm" style={{ color: PALETTE.accent }}>TM Hub</Link>
          <Link to="/blog" target="_blank" rel="noopener noreferrer" style={{ color: PALETTE.accent }}>Live blog ↗</Link>
        </div>

        {actionLog.length > 0 && (
          <div style={{ background: PALETTE.panelAlt, padding: 8, borderRadius: 4, marginBottom: 14, fontSize: '0.78rem', maxHeight: 80, overflowY: 'auto' }}>
            {actionLog.map((entry, i) => (
              <div key={i} style={{ color: entry.kind === 'error' ? PALETTE.error : PALETTE.muted, marginBottom: 2 }}>
                <code style={{ color: PALETTE.dim, marginRight: 8 }}>{entry.ts}</code>
                {entry.message}
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 4, marginBottom: 18, borderBottom: `1px solid ${PALETTE.border}`, flexWrap: 'wrap' }}>
          {STATUS_FILTERS.map(f => {
            const count = drafts.filter(f.predicate).length;
            return (
              <button key={f.id} onClick={() => { setStatusFilter(f.id); setSelected(null); }} style={{
                padding: '8px 16px', border: 'none', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600,
                borderRadius: '6px 6px 0 0',
                background: statusFilter === f.id ? PALETTE.panel : 'transparent',
                color: statusFilter === f.id ? PALETTE.text : PALETTE.dim,
              }}>{f.label} <span style={{ color: PALETTE.dim }}>({count})</span></button>
            );
          })}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 1.4fr' : '1fr', gap: 20 }}>
          <div>
            {filtered.length === 0 && !loading && (
              <div style={{
                padding: 40, background: PALETTE.panel, borderRadius: 8, color: PALETTE.dim,
                textAlign: 'center', border: `1px dashed ${PALETTE.border}`,
              }}>
                {drafts.length === 0
                  ? 'No drafts yet. Click "+ New post" to write one, or wait for the marketing-blog-post pipeline to produce a draft.'
                  : `No drafts in "${statusFilter}".`}
              </div>
            )}

            {filtered.map(d => (
              <div key={d.id}
                   onClick={() => setSelected(d)}
                   style={{
                     background: selected?.id === d.id ? '#243149' : PALETTE.panel,
                     borderRadius: 8, padding: 12, marginBottom: 8, cursor: 'pointer',
                     border: `1px solid ${selected?.id === d.id ? PALETTE.accent : PALETTE.border}`,
                   }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: PALETTE.text, fontWeight: 600, fontSize: '0.95rem' }}>
                      {d.title || '(untitled)'}
                    </div>
                    <div style={{ color: PALETTE.dim, fontSize: '0.72rem', marginTop: 2 }}>
                      /{d.slug || '(no slug)'} · {d.source || 'manual'}
                    </div>
                    {d.summary && (
                      <div style={{ color: PALETTE.muted, fontSize: '0.78rem', marginTop: 4, lineHeight: 1.4 }}>
                        {d.summary.substring(0, 140)}{d.summary.length > 140 ? '…' : ''}
                      </div>
                    )}
                  </div>
                  <span style={{
                    background: statusColor(d.status) + '22', color: statusColor(d.status),
                    padding: '3px 10px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 600,
                    whiteSpace: 'nowrap',
                  }}>
                    {d.status || 'draft'}
                  </span>
                </div>
                <div style={{ color: PALETTE.dim, fontSize: '0.7rem', marginTop: 6 }}>
                  {formatTimestamp(d.updatedAt || d.createdAt)}
                  {(d.tags || []).length > 0 && ` · ${(d.tags || []).join(', ')}`}
                </div>
              </div>
            ))}
          </div>

          {selected && (
            <DraftDrawer
              draft={selected}
              onClose={() => setSelected(null)}
              onSave={(v) => saveDraft(selected.id, v)}
              onMarkReady={() => markReady(selected.id)}
              onPublish={(v) => publishDraft(selected.id, v)}
              onDiscard={() => discardDraft(selected.id)}
              onUnpublish={() => unpublishDraft(selected.id, selected.publishedSlug || selected.slug)}
              onDelete={() => deleteDraft(selected.id)}
            />
          )}
        </div>
      </section>

      {showNewModal && (
        <NewPostModal
          defaultAuthor="Lawrence Norman"
          onSave={handleNewSave}
          onPublish={handleNewPublish}
          onCancel={() => setShowNewModal(false)}
        />
      )}
    </div>
  );
}
